/**
 * A jornada da clínica em números.
 *
 * Fica fora dos adaptadores porque os dois precisam do mesmo total — e porque
 * é daqui que sai o denominador da ocupação nos relatórios. Enquanto esta
 * conta não existia, "taxa de ocupação" era uma divisão sem divisor.
 */

import type { DiaDeFuncionamento, FaixaDeHorario } from './tipos';

/** `HH:MM` em minutos desde a meia-noite. */
export function emMinutos(hora: string): number {
    const [h, m] = hora.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}

export function minutosDaFaixa(faixa: FaixaDeHorario): number {
    return Math.max(0, emMinutos(faixa.fim) - emMinutos(faixa.inicio));
}

/** Minutos abertos num dia da semana; dia sem faixa é dia fechado. */
export function minutosDoDia(dias: DiaDeFuncionamento[], diaSemana: number): number {
    const dia = dias.find((d) => d.diaSemana === diaSemana);
    return (dia?.faixas ?? []).reduce((soma, f) => soma + minutosDaFaixa(f), 0);
}

export function horasSemanais(dias: DiaDeFuncionamento[]): number {
    const minutos = dias.reduce((soma, d) => soma + minutosDoDia(dias, d.diaSemana), 0);
    return Math.round((minutos / 60) * 10) / 10;
}

/**
 * Horas que a clínica abre entre dois dias, ambos incluídos.
 *
 * Percorre dia a dia em vez de multiplicar semanas: mês não tem número inteiro
 * de semanas, e um mês com cinco sábados abre mais que um com quatro.
 */
export function horasNoPeriodo(dias: DiaDeFuncionamento[], inicio: string, fim: string): number {
    let minutos = 0;
    /* Meio-dia, e não meia-noite: somar dias sobre a meia-noite tropeça no
       horário de verão, que existiu neste fuso e pode voltar. */
    const corrente = new Date(`${inicio}T12:00:00-03:00`);
    const ultimo = new Date(`${fim}T12:00:00-03:00`);
    while (corrente <= ultimo) {
        minutos += minutosDoDia(dias, corrente.getDay());
        corrente.setDate(corrente.getDate() + 1);
    }
    return Math.round((minutos / 60) * 10) / 10;
}

/** Ordena e recusa faixa vazia; a semana gravada é sempre legível de volta. */
export function normalizarDias(dias: DiaDeFuncionamento[]): DiaDeFuncionamento[] {
    return Array.from({ length: 7 }, (_, diaSemana) => ({
        diaSemana,
        faixas: (dias.find((d) => d.diaSemana === diaSemana)?.faixas ?? [])
            .filter((f) => f.inicio && f.fim && emMinutos(f.fim) > emMinutos(f.inicio))
            .sort((a, b) => emMinutos(a.inicio) - emMinutos(b.inicio)),
    }));
}

/**
 * A primeira sobreposição do dia, se houver.
 *
 * Faixas que se cruzam contariam a mesma hora duas vezes na ocupação — e a
 * clínica que "abre 60 horas por semana" com 40 de expediente é um número que
 * ninguém confere.
 */
export function faixaSobreposta(faixas: FaixaDeHorario[]): FaixaDeHorario | undefined {
    const ordenadas = [...faixas].sort((a, b) => emMinutos(a.inicio) - emMinutos(b.inicio));
    return ordenadas.find((f, i) => i > 0 && emMinutos(f.inicio) < emMinutos(ordenadas[i - 1]?.fim ?? '00:00'));
}

/**
 * A faixa de horas que a grade da agenda mostra.
 *
 * Sai da jornada declarada: era 8h às 18h fixo, e a clínica que fecha às 19h
 * via a última hora cortada. Agendamento fora da jornada ALARGA a grade em vez
 * de ficar desenhado fora dela. Sem jornada e sem agendamento, 8h às 18h.
 */
export function horasDaGrade(
    dias: DiaDeFuncionamento[],
    agendamentos: { inicioEm: string; duracaoMin: number }[] = [],
): { horaInicial: number; horaFinal: number } {
    const inicios: number[] = [];
    const fins: number[] = [];
    for (const dia of dias) {
        for (const f of dia.faixas) {
            if (minutosDaFaixa(f) <= 0) continue;
            inicios.push(emMinutos(f.inicio));
            fins.push(emMinutos(f.fim));
        }
    }
    for (const a of agendamentos) {
        const hora = new Date(a.inicioEm).toLocaleTimeString('en-GB', {
            timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        });
        const inicio = emMinutos(hora);
        inicios.push(inicio);
        fins.push(Math.min(24 * 60, inicio + Math.max(0, a.duracaoMin)));
    }
    if (inicios.length === 0) return { horaInicial: 8, horaFinal: 18 };

    const horaInicial = Math.floor(Math.min(...inicios) / 60);
    const horaFinal = Math.min(24, Math.max(horaInicial + 1, Math.ceil(Math.max(...fins) / 60)));
    return { horaInicial, horaFinal };
}
